import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from './ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import {
  Baby,
  Home,
  Users,
  MessageCircle,
  BarChart3,
  Settings,
  LogOut,
  User,
  Menu,
  ChevronLeft,
  ChevronRight,
  Brain,
  AlertCircle
} from 'lucide-react';
import { cn } from '../lib/utils';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/chat', icon: MessageCircle, label: 'AI Assistant' },
    { path: '/memories', icon: Brain, label: 'Memories' },
    { path: '/settings', icon: User, label: 'Settings' },
  ];

  if (user?.is_admin || user?.role === 'admin') {
    navLinks.push(
      { path: '/admin/llm-analytics', icon: BarChart3, label: 'LLM Analytics' },
      { path: '/admin/memory-analytics', icon: Brain, label: 'Memory Analytics' },
      { path: '/admin', icon: Settings, label: 'Admin Panel' }
    );
  }

  if (user?.is_superadmin || user?.role === 'superadmin') {
    navLinks.push(
      { path: '/superadmin/users', icon: Users, label: 'Users' },
      { path: '/superadmin/expert-ratings', icon: MessageCircle, label: 'Expert Ratings' },
      { path: '/superadmin/evaluations', icon: Brain, label: 'AI Evaluations' },
      { path: '/superadmin/ai-diagnostics', icon: AlertCircle, label: 'AI Diagnostics' }
    );
  }

  const SidebarContent = ({ isMobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={cn(
        "flex items-center p-6 border-b border-emerald-200/50",
        isCollapsed && !isMobile ? "justify-center" : "space-x-3"
      )}>
        <Link
          to="/dashboard"
          className="flex items-center space-x-3 group"
          onClick={() => isMobile && setMobileOpen(false)}
        >
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform">
            <Baby className="w-6 h-6 text-white" />
          </div>
          {(!isCollapsed || isMobile) && (
            <div className="font-display font-bold text-xl bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Nestology
            </div>
          )}
        </Link>
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-2">
        {navLinks.map(({ path, icon: Icon, label }) => (
          <Link
            key={path}
            to={path}
            onClick={() => isMobile && setMobileOpen(false)}
            className={cn(
              "flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all",
              isActive(path)
                ? 'bg-emerald-100 text-emerald-700'
                : 'text-slate-600 hover:text-emerald-600 hover:bg-emerald-50',
              isCollapsed && !isMobile ? 'justify-center' : ''
            )}
            title={isCollapsed && !isMobile ? label : ''}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {(!isCollapsed || isMobile) && <span>{label}</span>}
          </Link>
        ))}
      </nav>

      {/* User Menu */}
      <div className="p-4 border-t border-emerald-200/50">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                "w-full flex items-center space-x-3 hover:bg-emerald-50",
                isCollapsed && !isMobile ? 'justify-center px-2' : 'justify-start'
              )}
            >
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-white" />
              </div>
              {(!isCollapsed || isMobile) && (
                <div className="flex flex-col items-start flex-1 min-w-0">
                  <span className="font-medium text-slate-700 text-sm truncate w-full">
                    {user?.full_name}
                  </span>
                  {user?.is_admin && (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                      Admin
                    </span>
                  )}
                </div>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem className="flex items-center space-x-2 cursor-pointer">
              <User className="w-4 h-4" />
              <div>
                <p className="font-medium">{user?.full_name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleLogout}
              className="flex items-center space-x-2 cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Header with Hamburger */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-lg border-b border-emerald-200/50">
        <div className="flex items-center justify-between h-16 px-4">
          <Link to="/dashboard" className="flex items-center space-x-3 group">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform">
              <Baby className="w-6 h-6 text-white" />
            </div>
            <div className="font-display font-bold text-xl bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Nestology
            </div>
          </Link>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-emerald-50">
                <Menu className="w-6 h-6 text-slate-700" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-80">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <SheetDescription className="sr-only">
                Main navigation menu for the application
              </SheetDescription>
              <SidebarContent isMobile={true} />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div
        className={cn(
          "hidden lg:flex flex-col fixed left-0 top-0 h-screen bg-white border-r border-emerald-200/50 transition-all duration-300 z-40",
          isCollapsed ? "w-20" : "w-64"
        )}
      >
        <SidebarContent />

        {/* Collapse Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 w-6 h-6 rounded-full bg-white border border-emerald-200 hover:bg-emerald-50 shadow-sm"
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 text-slate-600" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-slate-600" />
          )}
        </Button>
      </div>
    </>
  );
};

export default Sidebar;
